import type { ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';

type ToastVariant = 'default' | 'destructive';

interface ToastOptions {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  duration?: number;
}

type ToastId = string | number;

const showToast = ({ title, description, variant, duration }: ToastOptions): ToastId => {
  const message = (title ?? description ?? '') as ReactNode;
  const options = {
    description: title ? description : undefined,
    duration,
  };

  return variant === 'destructive'
    ? sonnerToast.error(message as string, options)
    : sonnerToast(message as string, options);
};

function toast(options: ToastOptions) {
  const id = showToast(options);
  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: ToastOptions) =>
      sonnerToast(next.title as string, { id, description: next.description }),
  };
}

toast.success = (message: string) => sonnerToast.success(message);
toast.error = (message: string) => sonnerToast.error(message);

const dismissToast = (toastId?: ToastId) => sonnerToast.dismiss(toastId);

function useToast() {
  return {
    toasts: [] as never[],
    toast,
    dismiss: dismissToast,
  };
}

export { useToast, toast, dismissToast };
